import { useEffect, useState } from 'react';
import {
    getCurrentStoreData,
    updateStoreData,
    getCurrentStore,
} from '../services/auth';
import { checkInternetConnection } from '../utils/network';

const LAST_UPDATE_KEY = 'brico_store_data_last_update';
const UPDATE_INTERVAL = 60 * 60 * 1000;
// All hook instances await the same request instead of leaving secondary
// consumers permanently loading. Key by depot to avoid sharing another depot.
const pendingUpdates = new Map();

const hasData = (data) => data && Object.keys(data).length > 0;
const shouldUpdate = () => {
    try {
        const lastUpdate = Number(localStorage.getItem(LAST_UPDATE_KEY));
        return !lastUpdate || Date.now() - lastUpdate > UPDATE_INTERVAL;
    } catch (error) {
        return true;
    }
};

const refreshStore = (id) => {
    if (!pendingUpdates.has(id)) {
        const request = updateStoreData(id).finally(() =>
            pendingUpdates.delete(id),
        );
        pendingUpdates.set(id, request);
    }
    return pendingUpdates.get(id);
};

const useStoreData = () => {
    const [storeData, setStoreData] = useState(getCurrentStoreData);
    const [isLoading, setIsLoading] = useState(
        () => !hasData(getCurrentStoreData()),
    );
    const [error, setError] = useState(null);
    const [isOnline, setIsOnline] = useState(null);

    useEffect(() => {
        let active = true;
        let checking = false;
        const update = async (refresh = false) => {
            if (checking) return;
            checking = true;
            const cached = getCurrentStoreData();
            const { id } = getCurrentStore();
            // Show local data before any asynchronous network work.
            if (hasData(cached)) {
                setStoreData(cached);
                setIsLoading(false);
            }
            try {
                const status = await checkInternetConnection();
                if (!active) return;
                setIsOnline(status);
                // The probe only controls the indicator. Try the real service
                // when needed, even if the probe or navigator says offline.
                if (id && (refresh || !hasData(cached) || shouldUpdate())) {
                    const updated = await refreshStore(id);
                    if (!active) return;
                    if (hasData(updated)) {
                        setStoreData(updated);
                        setError(null);
                        // updateStoreData also returns cached data on failure;
                        // it records the timestamp only for a successful fetch.
                    } else if (!hasData(cached)) {
                        setError(
                            'Impossible de récupérer les données du magasin',
                        );
                    }
                }
            } catch (err) {
                if (active && !hasData(cached)) {
                    setError('Données du magasin indisponibles');
                }
            } finally {
                checking = false;
                if (active) setIsLoading(false);
            }
        };
        const onConnectionChange = () => update(true);
        update();
        const interval = window.setInterval(update, 30000);
        window.addEventListener('online', onConnectionChange);
        window.addEventListener('offline', onConnectionChange);
        return () => {
            active = false;
            window.clearInterval(interval);
            window.removeEventListener('online', onConnectionChange);
            window.removeEventListener('offline', onConnectionChange);
        };
    }, []);

    return { storeData, isLoading, error, isOnline };
};

export default useStoreData;
